import { ScrollArea } from '@/components/ui/scroll-area'
import { useAi } from '@/context/ai'
import type { EnrichedClassMtv4SearchResult } from '@/shared/api-generated-types'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import ClassCard from '@/widgets/v2/AI/Cards/Class'
import SkeletonCard from '@/widgets/v2/AI/Cards/Skeleton'
import uniqBy from 'lodash/uniqBy'
import { useEffect } from 'react'
import { gql, useQuery } from 'urql'

const MAX_RECOMMENDATIONS = 20
const MAX_DISPLAY_CARDS = 4
const MAX_CARD_HEIGHT = 125

const QUERY_SEARCH_AI_CLASSES = gql`
  query SearchAiClasses($input: AiSearchMTV4Input!) {
    searchAiClassesMTV4(input: $input) {
      items {
        id
        title
        imageUrl
        guideName
        guideAvatarUrl
        durationSeconds
        slug
        guideId
        bookmarked
      }
    }
  }
`

interface Props {
  tenantId?: string | null
}

const RelatedClasses = (props: Props): JSX.Element => {
  const {
    state: { query: searchQuery, relatedClasses },
    dispatch,
  } = useAi()
  const { state: bookmarkState } = useBookmarkItems()

  const [{ data, fetching }] = useQuery<{
    searchAiClassesMTV4: { items: EnrichedClassMtv4SearchResult[] }
  }>({
    query: QUERY_SEARCH_AI_CLASSES,
    pause: searchQuery.trim() === '',
    variables: { input: { query: searchQuery, limit: 5, tenant: props.tenantId } },
    requestPolicy: 'cache-first',
  })

  useEffect(() => {
    if (fetching || !data || !data.searchAiClassesMTV4?.items.length) return
    data.searchAiClassesMTV4.items.map((item) => relatedClasses.unshift(item))
    dispatch({
      type: 'UPDATE',
      payload: { relatedClasses: uniqBy(relatedClasses, 'id').slice(0, MAX_RECOMMENDATIONS) },
    })
  }, [data, fetching])

  return (
    <ScrollArea
      style={{
        height: `${MAX_CARD_HEIGHT * (relatedClasses.length > 0 ? MAX_DISPLAY_CARDS : 1)}px`,
      }}
    >
      {fetching && <SkeletonCard />}
      {relatedClasses.map((enrichedClass) => (
        <ClassCard
          key={enrichedClass.id}
          image={enrichedClass.imageUrl}
          {...enrichedClass}
          bookmarked={
            bookmarkState.classes?.[enrichedClass.id] !== undefined
              ? bookmarkState.classes?.[enrichedClass.id]
              : enrichedClass.bookmarked
          }
        />
      ))}
    </ScrollArea>
  )
}

export default RelatedClasses
